import axios from 'axios';
import 'url-search-params-polyfill';

import history from '../../utils/history';
import token from '../../utils/token';
import { getUsers } from './api/users';

import { SUPER_ADMIN_HOME_PATH, NEW_RETAILER_PATH } from '../../constants/pathRedirect';
import { SUPPLIER, RETAILER } from '../../constants/roles';
import { VALIDATED, VALIDATE_OTP } from '../../constants/auth';
import {
  UNAUTHENTICATED,
  REQUEST_AUTHENTICATED,
  AUTHENTICATED_ERROR,
  AUTHENTICATED_USER,
  SHOULD_RESET_PASSWORD,
} from '../constants/authTypes';
import {
  setRolesUpdateRole,
  setRolesSupplierId,
  setRolesRetailerId,
  rolesClearPermissions,
  setRolesWithUser,
} from './roles';
import { alertError } from './alert';
import { IGNORE_PREVIOUS_LOCATION, PREVIOUS_LOCATION } from '../../constants/localStorageKeys';

export const authenticated = (currentStep) => ({ type: AUTHENTICATED_USER, payload: { currentStep } });
export const unauthenticated = { type: UNAUTHENTICATED };
export const requestAuthenticated = (email) => ({ type: REQUEST_AUTHENTICATED, payload: email });
export const authenticatedError = (error) => ({ type: AUTHENTICATED_ERROR, payload: error });
export const shouldResetPassword = (value) => ({ type: SHOULD_RESET_PASSWORD, payload: value });

const scopes = [
  'read:suppliers',
  'read:retailers',
  'write:retailers',
  'read:users',
  'write:suppliers',
  'write:users',
  'read:variants',
  'write:variants',
  'write:warehouses',
  'read:warehouses',
  'write:inventory',
  'read:inventory',
  'write:orders',
  'read:orders',
  'write:fulfillments',
  'read:system_properties',
  'write:support',
  'read:channels',
];

export const signIn = (values) => (dispatch) => {
  dispatch(rolesClearPermissions);
  dispatch(requestAuthenticated(values.username));

  const params = new URLSearchParams();
  params.append('client_id', process.env.REACT_APP_CLIENT_ID);
  params.append('client_secret', process.env.REACT_APP_CLIENT_SECRET);
  params.append('grant_type', 'password');
  params.append('username', values.username);
  params.append('password', values.password);
  params.append('scope', scopes.join(' '));

  axios
    .post(process.env.REACT_APP_TOKEN_URL, params)
    .then((response) => {
      const data = response.data;
      if (data) {
        token.setToken(data);
        const nextStep = data.is_otp_needed ? VALIDATE_OTP : VALIDATED;
        dispatch(authenticated(nextStep));
        return data;
      }
    })
    .catch((error) => {
      const authenticationError = {};
      if (error.response?.data) {
        authenticationError.type = error.response.data.error;
        authenticationError.description = error.response.data.error_description;
      }
      dispatch(authenticatedError(authenticationError));
    });
};

const getPreviousLocation = () => {
  const location = sessionStorage.getItem(PREVIOUS_LOCATION);
  const ignorePrevLocation = sessionStorage.getItem(IGNORE_PREVIOUS_LOCATION);
  sessionStorage.removeItem(PREVIOUS_LOCATION);
  sessionStorage.removeItem(IGNORE_PREVIOUS_LOCATION);
  return !ignorePrevLocation ? location : null;
};

export const signInFlow = () => async (dispatch, getState) => {
  const { role } = getState();
  dispatch(shouldResetPassword(false));
  try {
    const response = await getUsers();
    const user = response.data;
    const { suppliers, retailers } = user;

    const latestUser = role.users[user.id];

    dispatch(setRolesWithUser(user));

    const hasSuppliers = suppliers.length > 0;
    const hasRetailers = retailers.length > 0;

    const location = getPreviousLocation();
    if (location) {
      history.push(location);
      return;
    }

    if (user.is_super_admin) {
      history.push(SUPER_ADMIN_HOME_PATH);
      return;
    }

    if (!hasSuppliers && !hasRetailers) {
      history.push(NEW_RETAILER_PATH);
      return;
    }

    if (latestUser && latestUser.currentRole === SUPPLIER && latestUser.supplierId) {
      dispatch(setRolesSupplierId(latestUser.supplierId));
      history.push(`/suppliers/${latestUser.supplierId}/retailers`);
      return false;
    } else if (latestUser && latestUser.currentRole === RETAILER && latestUser.retailerId) {
      dispatch(setRolesRetailerId(latestUser.retailerId));
      history.push(`/retailers/${latestUser.retailerId}/catalogue-management`);
      return false;
    } else if (hasSuppliers) {
      const supplier = suppliers[0];
      dispatch(setRolesUpdateRole(supplier.id, SUPPLIER));
      history.push(`/suppliers/${supplier.id}/retailers`);
    } else if (hasRetailers) {
      const retailer = retailers[0];
      dispatch(setRolesUpdateRole(retailer.id, RETAILER));

      const hasReplenishPermissions = retailers.every((item) => item.replenish_permission);
      history.push(
        hasReplenishPermissions
          ? `/retailers/${retailer.id}/reorder`
          : `/retailers/${retailer.id}/catalogue-management`,
      );
    }

    return response;
  } catch (err) {
    if (err?.response?.data?.message) {
      dispatch(alertError(err));
      dispatch(unauthenticated);
      history.push('/signin');
    } else {
      console.error(err);
    }
  }
};

export const logout = () => async (dispatch) => {
  dispatch(rolesClearPermissions);

  token.removeToken();
  sessionStorage.setItem(IGNORE_PREVIOUS_LOCATION, 'true');
  dispatch(unauthenticated);
};
